/**
 * The assembled content-pack schema — everything a game ships as authored
 * JSON (plan §3; architecture.md §5).
 *
 * @remarks
 * This is the shape `content-loader` validates in R3 before semantic
 * cross-checks (dangling refs, duplicate ids) and lexicon compilation.
 * `rooms` requires at least one entry because a pack without rooms cannot
 * satisfy its own `entryRoomId`; every OTHER referential guarantee (entry
 * room exists, placements name declared items, locks name declared flags) is
 * the loader's semantic layer — schemas validate shape, never reachability.
 * The `vocabulary` is the pack's authored lexicon; two-layer base-pack
 * merging (decision D16) happens in the loader before `compileLexicon`.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { actorDefinitionSchema } from './actors.js';
import { audioDefinitionSchema, conditionalAmbienceSchema } from './audio.js';
import { counterDefinitionSchema } from './counters.js';
import { defaultResponseOverridesSchema } from './default-responses.js';
import { endingDefinitionSchema } from './endings.js';
import { eventDefinitionSchema } from './events.js';
import { flagDefinitionSchema } from './flags.js';
import { groupDefinitionSchema, pluralAnswerSchema } from './groups.js';
import { itemIdSchema } from './identifiers.js';
import { imageDefinitionSchema } from './images.js';
import { itemDefinitionSchema } from './items.js';
import { packManifestSchema } from './manifest.js';
import { packPacingSchema } from './pacing.js';
import { paywallSchema } from './paywall.js';
import { nonBlankStringSchema } from './primitives.js';
import { reactionResponsesSchema } from './reactions.js';
import { recordDeclarationSchema } from './record.js';
import { roomDefinitionSchema } from './rooms.js';
import { speakerDefinitionSchema } from './speakers.js';
import { spriteDefinitionSchema } from './sprites.js';
import { triggerDefinitionSchema } from './triggers.js';
import { lexiconDefinitionSchema } from './vocabulary.js';
import { voiceLineDefinitionSchema } from './voices.js';

/** One complete authored content pack: manifest, world, flags, and vocabulary. */
export const contentPackDefinitionSchema = z
	.strictObject({
		/**
		 * D232: the pack's actors — embodied things with a location, a NEW
		 * entity kind beside items (`actors.ts`). Top-level like items and
		 * rooms; an actor is placed by its own `initialRoomId`, never by a
		 * room's `itemPlacements`, because where it stands is state the world
		 * can change (round C) and a placement list is where things hold still.
		 */
		actors: z.array(actorDefinitionSchema).readonly().default([]),
		/**
		 * Items in scope in EVERY room (D19, R4): referable, inspectable,
		 * askable everywhere; never placeable (ambient XOR placed — the loader
		 * enforces it) and never takeable. EURYDICE is the founding use: she is
		 * not IN six rooms, she is ambient. Placement keeps meaning LOCATION.
		 */
		/**
		 * R35 (D115): the pack-level ambience — the building's sound, the
		 * default every room inherits unless it carries its own override.
		 * Absent means the pack has no audio layer at all (like a pack with no
		 * images); `'silence'` anywhere in the declaration means quiet ON
		 * PURPOSE (Calder's §6.2 rule — see `audio.ts`).
		 */
		ambience: conditionalAmbienceSchema.optional(),
		ambientItemIds: z.array(itemIdSchema).readonly().default([]),
		/**
		 * R35 (D115): the pack's audio table — assets declared once, referenced
		 * by the pack's and rooms' `ambience`; the loader cross-checks both
		 * directions, exactly as it does plates and sprites.
		 */
		audio: z.array(audioDefinitionSchema).readonly().default([]),
		/**
		 * D244: the amounts the world holds — declared once, each with its
		 * range, its start, its scope at the checkpoint and what happens at
		 * its floor and ceiling (see `counters.ts`). Moved only by
		 * `adjust_counter` / `set_counter` and a stock's take; read by
		 * `counter_at_least`, a countdown, and `{counter:<id>}` in prose.
		 */
		counters: z.array(counterDefinitionSchema).readonly().default([]),
		/**
		 * D25: the pack's claims on the engine's fallback voice — any subset of
		 * response slots re-voiced with authored lines; the engine's built-in
		 * neutral table covers the rest.
		 */
		defaultResponses: defaultResponseOverridesSchema.optional(),
		/** R11: how this pack's story can conclude — selected by `set_ending`. */
		endings: z.array(endingDefinitionSchema).readonly().default([]),
		/**
		 * D143: the slots a thing can be equipped into — `face`, `hand`, `body`;
		 * the pack names them, the engine holds one item per slot. Declared once
		 * here so an item's `equippable.slot` can be validated against the roster
		 * and a slot nothing uses can be refused as dead (D47).
		 */
		equipmentSlots: z.array(nonBlankStringSchema).readonly().default([]),
		/** R9: the scheduler's event definitions — armed by `schedule_event`, fired by the tick. */
		events: z.array(eventDefinitionSchema).readonly().default([]),
		flags: z.array(flagDefinitionSchema).readonly().default([]),
		/**
		 * D217: things a plural can name together, and what the pack says about
		 * them together — see `groups.ts`. Items join by `groupId`; the loader
		 * refuses a group nothing joins.
		 */
		groups: z.array(groupDefinitionSchema).readonly().default([]),
		/**
		 * R23 (D89): the pack's plate table — `alt` and `depicts` belong to the
		 * PICTURE, not to a room's use of it, so a plate serving two rooms is
		 * declared once. Rooms reference entries through their `image` field;
		 * the loader cross-checks both directions.
		 */
		images: z.array(imageDefinitionSchema).readonly().default([]),
		items: z.array(itemDefinitionSchema).readonly().default([]),
		manifest: packManifestSchema,
		/**
		 * D153: the pack's real-time clock — a conditional pace the shell
		 * honors as an auto-`wait`, a countdown named by its deadline event,
		 * and whether the clock ships on. Absent means the pack has no clock at
		 * all; the engine's clock is turns either way (see `pacing.ts`).
		 */
		pacing: packPacingSchema.optional(),
		/**
		 * D165 (platform.md §4): what the game says when a turn would carry the
		 * player past the demo without an entitlement — "the game's own line
		 * for it, not a modal". Required exactly when a room is `paid`; the
		 * loader refuses a paywall with no words and words with no wall.
		 */
		paywall: paywallSchema.optional(),
		/**
		 * D217: how a SET is answered — one line for the set, each member in
		 * turn, or each up to a count (see `groups.ts`). The builder's choice;
		 * absent, one line for the set, which is what the demos chose.
		 */
		plurals: pluralAnswerSchema.default({ answer: 'set' }),
		/**
		 * D27: exact-input reaction one-liners, consulted only when the whole
		 * input failed to parse — easter eggs that can never shadow commands.
		 */
		reactions: reactionResponsesSchema.optional(),
		/**
		 * D241: the Record — what a finished run leaves behind for the next
		 * (the remembered flags) and which endings leave it (see `record.ts`).
		 * Absent, the game remembers nothing across runs, and `record_holds`
		 * is refused at load.
		 */
		record: recordDeclarationSchema.optional(),
		rooms: z.array(roomDefinitionSchema).min(1).readonly(),
		/**
		 * D192: the voices the pack speaks in — each a channel with an index
		 * and a quotation law. Optional (Mike's ruling): a pack may declare
		 * none, and then every line is in one of the engine's four fixed
		 * sources. The loader refuses a `source` naming no declared speaker.
		 */
		speakers: z.array(speakerDefinitionSchema).readonly().default([]),
		/**
		 * R32 (D96): the pack's sprite table — scene nodes composited over room
		 * plates while their conditions hold. Declared pack-level like `images`
		 * and for the same reason; rooms bind entries through their `sprites`
		 * field, and the loader cross-checks both directions.
		 */
		sprites: z.array(spriteDefinitionSchema).readonly().default([]),
		/** R11: standing condition-watchers; R12 added occurrence matchers. */
		triggers: z.array(triggerDefinitionSchema).readonly().default([]),
		vocabulary: lexiconDefinitionSchema,
		/** R12: speakable voice lines with their own policy — uttered by `speak_voice_line`. */
		voiceLines: z.array(voiceLineDefinitionSchema).readonly().default([]),
	})
	.readonly()
	// R23 (D89): a pack that declares plates must declare their one aspect
	// ratio — a cross-field shape rule (like an exit's destination pair), not a
	// reachability question, so it lives here rather than in the loader.
	.refine((contentPack) => contentPack.images.length === 0 || contentPack.manifest.imageAspectRatio !== undefined, {
		message: 'a pack that declares images must declare manifest.imageAspectRatio — the layout must not jump between rooms',
		path: ['manifest', 'imageAspectRatio'],
	});

/** One complete authored content pack — see {@link contentPackDefinitionSchema}. */
export type ContentPackDefinition = z.infer<typeof contentPackDefinitionSchema>;
